import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAssessment } from '../context/AssessmentContext';
import { Activity, FileText, LogOut, TrendingUp, Play, Eye } from 'lucide-react';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser, signOut, setUser } = useAuth();
  const { startAssessment } = useAssessment();
  const [activeTab, setActiveTab] = useState('start');
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);

  // Ensure client-side only rendering to prevent hydration errors
  useEffect(() => {
    console.log('🏠 UserDashboard mounting...');
    setIsClient(true);
  }, []);

  useEffect(() => {
    console.log('🔍 UserDashboard auth check:', {
      authUser: authUser ? authUser.email : 'null',
      loading: loading,
      localStorage: localStorage.getItem('auth_user') ? 'has data' : 'empty',
      cookies: document.cookie.includes('stack-access') ? 'has Stack cookies' : 'no Stack cookies'
    });
  }, [authUser, loading]);

  useEffect(() => {
    // Check if user is in localStorage (from Stack Auth)
    const checkAuth = async () => {
      if (!authUser) {
        // Try to get from localStorage
        const storedUser = localStorage.getItem('auth_user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            setUser(user);
            setLoading(false);
            return;
          } catch (e) {
            console.error('Error parsing stored user:', e);
          }
        }
        
        // Check if Stack Auth cookie exists
        const hasStackCookie = document.cookie.includes('stack-');
        if (hasStackCookie) {
          // Stack Auth is present but user not in localStorage yet
          // Try to fetch user info from Stack Auth API or wait
          console.log('Stack Auth detected, waiting for user sync...');
          setTimeout(() => {
            // Check again after delay
            const storedUserRetry = localStorage.getItem('auth_user');
            if (storedUserRetry) {
              try {
                const user = JSON.parse(storedUserRetry);
                setUser(user);
                setLoading(false);
                return;
              } catch (e) {
                console.error('Error parsing stored user on retry:', e);
              }
            }
            // Still no user, redirect
            navigate('/');
          }, 2000);
        } else {
          // No Stack Auth, redirect immediately
          navigate('/');
        }
      } else {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [authUser, navigate, setUser]);

  useEffect(() => {
    if (!authUser || loading) return;

    // Load user's assessments from database
    const loadAssessments = async () => {
      if (!authUser) return;
      
      console.log('Loading assessments for user:', authUser.email);
      
      try {
        // Fetch from database API
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'assessment.list',
            data: { email: authUser.email }
          })
        });

        console.log('API response status:', response.status);
        
        if (response.ok) {
          const result = await response.json();
          console.log('API response:', result);
          const dbAssessments = result.assessments || [];
          setAssessments(dbAssessments);
          console.log('Loaded assessments from database:', dbAssessments.length);
          
          // Also check localStorage
          const stored = localStorage.getItem('health_assessments');
          if (stored) {
            const allAssessments = JSON.parse(stored);
            const userAssessments = Object.values(allAssessments).filter(
              a => a.personalInfo?.email === authUser.email
            );
            console.log('Found in localStorage:', userAssessments.length);
            
            // If database is empty but localStorage has data, use localStorage
            if (dbAssessments.length === 0 && userAssessments.length > 0) {
              console.log('Using localStorage assessments instead');
              setAssessments(userAssessments);
            }
          }
        } else {
          const errorText = await response.text();
          console.error('API error:', response.status, errorText);
          
          // Fallback to localStorage
          const stored = localStorage.getItem('health_assessments');
          if (stored) {
            const allAssessments = JSON.parse(stored);
            const userAssessments = Object.values(allAssessments).filter(
              a => a.personalInfo?.email === authUser.email
            );
            console.log('Falling back to localStorage:', userAssessments.length);
            setAssessments(userAssessments);
          }
        }
      } catch (error) {
        console.error('Error loading assessments:', error);
        // Fallback to localStorage
        const stored = localStorage.getItem('health_assessments');
        if (stored) {
          const allAssessments = JSON.parse(stored);
          const userAssessments = Object.values(allAssessments).filter(
            a => a.personalInfo?.email === authUser.email
          );
          console.log('Exception - falling back to localStorage:', userAssessments.length);
          setAssessments(userAssessments);
        }
      }
    };

    loadAssessments();
  }, [authUser, loading, navigate]);

  const handleLogout = async () => {
    await signOut();
    // Use replace to prevent back button issues and immediate navigation
    navigate('/', { replace: true });
  };

  const handleStartAssessment = async () => {
    // Start assessment directly with user data
    const personalData = {
      email: authUser?.email || '',
      firstName: authUser?.displayName?.split(' ')[0] || 'Student',
      lastName: authUser?.displayName?.split(' ').slice(1).join(' ') || '',
      sport: authUser?.sport || 'Athletics',
      schoolName: authUser?.schoolName || 'School',
      grade: authUser?.grade || '10',
      section: authUser?.section || 'A',
      age: authUser?.age || 15
    };
    
    try {
      // Start assessment with personal data
      await startAssessment(personalData);
      // Navigate directly to assessment
      navigate('/assessment');
    } catch (error) {
      console.error('Error starting assessment:', error);
    }
  };

  // Prevent SSR hydration mismatch - show loading until client-side mount
  if (!isClient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading while checking auth
  if (loading || !authUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Syncing user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-400" />
              <div>
                <h1 className="text-xl font-bold text-white">Splink Pro</h1>
                <p className="text-xs text-slate-400">Health Assessment Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">
                  {authUser?.displayName || 'User'}
                </p>
                <p className="text-xs text-slate-400">{authUser?.email}</p>
              </div>
              {authUser?.role === 'admin' && (
                <button
                  onClick={() => navigate('/db-analytics')}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all shadow-lg"
                >
                  <Activity className="w-4 h-4" />
                  <span>DB Analytics</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex gap-4 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('start')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'start'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Play className="w-5 h-5" />
            <span>Start Assessment</span>
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'reports'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>My Reports</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'start' && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
                <div className="text-center mb-6">
                  <Activity className="w-16 h-16 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold mb-2">Ready to Begin?</h2>
                  <p className="text-blue-100">
                    Complete your comprehensive health assessment in three parts:
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-1">1. Nutrition Assessment</h3>
                    <p className="text-sm text-blue-100">20 questions about your dietary habits</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-1">2. Cognitive Assessment</h3>
                    <p className="text-sm text-blue-100">25 questions about your personality and learning style</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-1">3. Physical Assessment</h3>
                    <p className="text-sm text-blue-100">7 camera-based fitness tests (choose 4)</p>
                  </div>
                </div>

                <button
                  onClick={handleStartAssessment}
                  className="w-full bg-white text-blue-600 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Start New Assessment
                </button>

                <p className="text-center text-sm text-blue-100 mt-4">
                  ⏱️ Estimated time: 10-15 minutes
                </p>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Your Assessment Reports</h2>
              
              {assessments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 text-lg mb-2">No reports yet</p>
                  <p className="text-slate-500 text-sm mb-6">
                    Complete an assessment to see your personalized reports
                  </p>
                  <button
                    onClick={() => setActiveTab('start')}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Start Your First Assessment
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {assessments.map((assessment) => (
                    <div
                      key={assessment.id}
                      className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 hover:border-slate-600 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-1">
                            Assessment - {new Date(assessment.created_at || assessment.createdAt).toLocaleDateString()}
                          </h3>
                          <p className="text-sm text-slate-400">
                            {(assessment.personal_info || assessment.personalInfo)?.firstName} {(assessment.personal_info || assessment.personalInfo)?.lastName}
                          </p>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => navigate(`/report/${assessment.id}/nutrition`)}
                              disabled={!(assessment.nutrition_responses || assessment.nutritionResponses) || (assessment.nutrition_responses || assessment.nutritionResponses).length === 0}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                (assessment.nutrition_responses || assessment.nutritionResponses)?.length > 0
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 cursor-pointer'
                                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                              }`}
                            >
                              Nutrition {(assessment.nutrition_responses || assessment.nutritionResponses)?.length > 0 ? '✓' : '○'}
                            </button>
                            <button
                              onClick={() => navigate(`/report/${assessment.id}/cognitive`)}
                              disabled={!(assessment.cognitive_responses || assessment.cognitiveResponses) || (assessment.cognitive_responses || assessment.cognitiveResponses).length === 0}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                (assessment.cognitive_responses || assessment.cognitiveResponses)?.length > 0
                                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 cursor-pointer'
                                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                              }`}
                            >
                              Cognitive {(assessment.cognitive_responses || assessment.cognitiveResponses)?.length > 0 ? '✓' : '○'}
                            </button>
                            <button
                              onClick={() => navigate(`/report/${assessment.id}/physical`)}
                              disabled={!(assessment.physical_tests || assessment.physicalTests) || (assessment.physical_tests || assessment.physicalTests).length === 0}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                (assessment.physical_tests || assessment.physicalTests)?.length > 0
                                  ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 cursor-pointer'
                                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                              }`}
                            >
                              Physical {(assessment.physical_tests || assessment.physicalTests)?.length > 0 ? '✓' : '○'}
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/report/${assessment.id}`)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Full Report</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
